import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// A recovery link lands on the app with `type=recovery` in the URL, and the
// client immediately trades it for a real session. That session is
// indistinguishable from a normal login, so without this flag the app would
// drop the user straight onto the home screen and never offer the password
// form the link was for.
//
// Read at module load, before anything awaits: `detectSessionInUrl` strips the
// hash once it processes it, so by the time an effect runs the evidence can be
// gone. The PASSWORD_RECOVERY event below is the canonical signal and covers
// the case where this read is too late; the two together mean the flow cannot
// be missed in either direction.
const RECOVERY_IN_URL = (() => {
  if (typeof window === 'undefined') return false;
  const test = /(?:^|[#&?])type=recovery(?:&|$)/;
  return test.test(window.location.hash || '') || test.test(window.location.search || '');
})();

export function useAuth() {
  // undefined = still resolving, null = not logged in, object = logged in
  const [session, setSession] = useState(undefined);
  const [recovery, setRecovery] = useState(RECOVERY_IN_URL);

  useEffect(() => {
    if (!supabase) {
      setSession(null);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
      setSession(session ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password) => {
    if (!supabase) return { message: 'service-unavailable' };
    const { error } = await supabase.auth.signUp({ email, password });
    return error;
  };

  const signIn = async (email, password) => {
    if (!supabase) return { message: 'service-unavailable' };
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error;
  };

  // Guest mode: Supabase built-in anonymous sign-in. No shared password, no key
  // in the client. The returned session's user.is_anonymous === true, which the
  // app uses to seed a demo portfolio, show the guest banner, and block account
  // management. RLS still applies (auth.uid() is a real UUID for anon users).
  const signInAnonymously = async () => {
    if (!supabase) return { message: 'service-unavailable' };
    const { error } = await supabase.auth.signInAnonymously();
    return error;
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  };

  // Sends the recovery mail. `redirectTo` is the app's own origin, which keeps
  // localhost and production on one code path and needs no build-time constant
  // — but the value still has to appear in Supabase's Redirect URLs allow-list
  // or the link in the mail refuses to open.
  //
  // Supabase answers the same way whether or not the address has an account, on
  // purpose: a different answer would turn this form into a way to test which
  // emails are registered. The caller must word its confirmation to match, and
  // must not claim a mail was sent.
  const resetPassword = async (email) => {
    if (!supabase) return { message: 'service-unavailable' };
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    return error;
  };

  // Sets the new password on the session the recovery link opened.
  const updatePassword = async (password) => {
    if (!supabase) return { message: 'service-unavailable' };
    const { error } = await supabase.auth.updateUser({ password });
    return error;
  };

  // Leaves the recovery screen. The URL fragment is cleared too, so a reload
  // does not drop the user back into a password form they already finished.
  const endRecovery = () => {
    setRecovery(false);
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  return {
    session,
    loading: session === undefined,
    degraded: !supabase,
    recovery,
    signUp,
    signIn,
    signInAnonymously,
    signOut,
    resetPassword,
    updatePassword,
    endRecovery,
  };
}
